import { CommonModule } from '@angular/common';
import { Component, effect, input, OnInit, output } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { SiteService } from '@app/api/site.service';
import { AndroWebCoreBase } from '@app/core/AndroWebCoreBase';
import { ISiteMapDetails } from '@app/models/ISiteMapDetails';
import { OrderOccasion } from '@app/models/order-occasion';
import { Site } from '@app/models/site';
import { IDayPeriod } from '@app/models/site/IDayPeriod';
import { IDisplayedOpeningHours } from '@app/models/site/IDisplayedOpeningHours';
import { StoreHelperService } from '@app/shared/services/store-helper/store-helper.service';
import { filter, shareReplay } from 'rxjs';

@Component({
  selector: 'app-home-page-store-dropdown',
  templateUrl: './home-page-store-dropdown.component.html',
  styleUrls: ['./home-page-store-dropdown.component.scss'],
  imports: [ReactiveFormsModule, MatFormFieldModule, MatSelectModule, CommonModule]
})
export class HomePageStoreDropdownComponent extends AndroWebCoreBase {
  public onSelection = output<Site>();
  public stores = input<Site[]>();
  public occasion = input<OrderOccasion>();

  public selectedStore: Site;
  public openingTimeStatus: { value: string; isOpen: boolean; openLater: boolean };

  private readonly _detailedSites = toSignal(
    this._siteService.detailedSites$.pipe(
      filter((s) => s.length > 0),
      shareReplay({ bufferSize: 1, refCount: true })
    ),
    { initialValue: [] as ISiteMapDetails[] }
  );

  constructor(private _siteService: SiteService, private _storeHelperService: StoreHelperService) {
    super();

    effect(() => {
      const sites = this.stores() ?? [];

      if (sites.length === 1) {
        this.setSelectedStore(sites[0]);
      }
    });
  }

  /**
   * returns the phone number with the HTML tel marker to allow it function as a clickable phone number
   * @param number - the phone number
   */
  public getPhoneNumber(number: string): string {
    return `tel:${number?.replace(/\s/g, '')}`;
  }

  /**
   * outputs the selected site.
   * @param site
   */
  public setSelectedStore(site: Site): void {
    this.selectedStore = site;

    const detailedSite = this._detailedSites().find((x) => x.id === site.Id);
    if (!detailedSite) {
      return;
    }

    const { occasionsSupported, occasionOpeningHours } = detailedSite;
    const supportsDelivery = occasionsSupported.includes(OrderOccasion.Delivery);
    const supportsCollection = occasionsSupported.includes(OrderOccasion.Collection);
    const supportsDineIn = occasionsSupported.includes(OrderOccasion.DineIn);

    const hasValues = (periods: IDayPeriod[]) => periods?.length >= 0; // Empty array is full week closed

    const showOccasionOpeningTimes =
      occasionOpeningHours.some((hours: IDisplayedOpeningHours) => hasValues(hours.delivery)) ||
      occasionOpeningHours.some((hours: IDisplayedOpeningHours) => hasValues(hours.collection)) ||
      occasionOpeningHours.some((hours: IDisplayedOpeningHours) => hasValues(hours.dineIn));

    const allOccasionsSet =
      occasionOpeningHours.some((hours: IDisplayedOpeningHours) => hasValues(hours.delivery)) &&
      occasionOpeningHours.some((hours: IDisplayedOpeningHours) => hasValues(hours.collection)) &&
      occasionOpeningHours.some((hours: IDisplayedOpeningHours) => hasValues(hours.dineIn));

    this.openingTimeStatus = this._storeHelperService.getStoreOpeningTimeStatus(
      detailedSite,
      showOccasionOpeningTimes,
      allOccasionsSet,
      supportsDelivery,
      supportsCollection,
      supportsDineIn,
      this.occasion()
    );
  }

  public onOrderNow(site: Site): void {
    this.onSelection.emit(site);
  }
}
